import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../lib/api-client';

interface Props {
  open: boolean;
  onClose: () => void;
  userId: string | null;
  userName: string;
  userOrgId: string;
}

interface AutomationRow {
  id: string;
  name: string;
  slug: string;
  organizationId: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'RETIRED';
}

interface AccessRow {
  automationId: string;
  canTrigger: boolean;
}

interface AccessFromApi {
  automationId: string;
  canTrigger: boolean;
  automation: { id: string; name: string; slug: string; organizationId: string };
}

function apiErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: string } } } | null)?.response?.data;
  return data?.message ?? 'Action impossible. Réessayez dans un instant.';
}

export function AutomationAccessModal({ open, onClose, userId, userName, userOrgId }: Props) {
  const qc = useQueryClient();
  const [accesses, setAccesses] = useState<Map<string, boolean>>(new Map());
  const [error, setError] = useState<string | null>(null);

  // Toutes les automatisations visibles (l'API filtre déjà selon le rôle).
  // On re-filtre côté client sur l'org de l'utilisateur cible pour ne proposer
  // que ce qui est légitime (utile pour SUPER_ADMIN / ADWEB_OPERATOR qui voient
  // toutes les orgs).
  const allAutomations = useQuery({
    queryKey: ['automations', 'for-access'],
    queryFn: async () =>
      (await apiClient.get<{ data: AutomationRow[] }>('/automations?page=1&pageSize=100')).data
        .data,
    enabled: open,
  });

  const currentAccess = useQuery({
    queryKey: ['user-automation-access', userId],
    queryFn: async () =>
      (await apiClient.get<AccessFromApi[]>(`/users/${userId}/automation-access`)).data,
    enabled: open && !!userId,
  });

  // Initialise la map des accès dès que les deux requêtes sont prêtes.
  useEffect(() => {
    if (!open) return;
    if (currentAccess.data) {
      const m = new Map<string, boolean>();
      for (const a of currentAccess.data) m.set(a.automationId, a.canTrigger);
      setAccesses(m);
    }
    setError(null);
  }, [open, currentAccess.data]);

  const save = useMutation({
    mutationFn: async () => {
      if (!userId) return;
      const payload: { accesses: AccessRow[] } = {
        accesses: Array.from(accesses.entries())
          .filter(([, can]) => can)
          .map(([automationId, canTrigger]) => ({ automationId, canTrigger })),
      };
      await apiClient.put(`/users/${userId}/automation-access`, payload);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['user-automation-access', userId] });
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err)),
  });

  const orgAutomations = (allAutomations.data ?? []).filter((a) => a.organizationId === userOrgId);
  const loading = currentAccess.isLoading || allAutomations.isLoading;

  function toggle(automationId: string) {
    setAccesses((prev) => {
      const next = new Map(prev);
      next.set(automationId, !(prev.get(automationId) ?? false));
      return next;
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Accès aux automatisations — ${userName}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || loading}>
            {save.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            {error}
          </div>
        )}
        <p className="text-xs text-slate-500">
          Cochez les automatisations que cet utilisateur peut <strong>voir et lancer</strong>. Une
          automatisation décochée n’apparaît plus du tout pour lui.
        </p>

        {loading ? (
          <p className="text-sm text-slate-400">Chargement…</p>
        ) : orgAutomations.length === 0 ? (
          <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
            Aucune automatisation dans l'organisation de cet utilisateur.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {orgAutomations.map((a) => {
              const can = accesses.get(a.id) ?? false;
              return (
                <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm text-slate-900">{a.name}</div>
                    <div className="truncate font-mono text-[11px] text-slate-500">{a.slug}</div>
                  </div>
                  <label className="flex shrink-0 items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={can}
                      onChange={() => toggle(a.id)}
                      className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                    />
                    Peut exécuter
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
